import { Layers, Package, Boxes, X, ChevronRight } from 'lucide-react';
import type { EntityImportConfig } from './types';
import {
  categoryImportConfig,
  productImportConfig,
  productVariantImportConfig,
} from './entities/productImportConfigs';

const OPTIONS: {
  id: string;
  step: string;
  config: EntityImportConfig;
  icon: typeof Package;
  tip: string;
}[] = [
  {
    id: 'category',
    step: '1',
    config: categoryImportConfig,
    icon: Layers,
    tip: 'Optional if categories already exist, or will be auto-created with products.',
  },
  {
    id: 'product',
    step: '2',
    config: productImportConfig,
    icon: Package,
    tip: 'Import products & services. Use Has Variants = 1 when you will add variants next.',
  },
  {
    id: 'product_variant',
    step: '3',
    config: productVariantImportConfig,
    icon: Boxes,
    tip: 'Import size/style/options linked by Parent Item Name. Parents must already exist with Has Variants = 1.',
  },
];

interface ItemsImportChooserProps {
  onSelect: (config: EntityImportConfig) => void;
  onClose: () => void;
}

export default function ItemsImportChooser({ onSelect, onClose }: ItemsImportChooserProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="relative inline-block w-full max-w-2xl p-6 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Import Products</h3>
              <p className="text-sm text-gray-500 mt-1">
                Choose what to import. Recommended order: Categories → Products → Variants.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-6 space-y-3">
            {OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onSelect(opt.config)}
                  className="w-full flex items-start gap-4 p-4 rounded-xl border border-gray-200 hover:border-primary-500 hover:bg-primary-50 text-left transition-colors group"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 text-primary-700 font-bold text-sm shrink-0">
                    {opt.step}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-primary-600" />
                      <span className="font-semibold text-gray-900">{opt.config.title}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{opt.tip}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-primary-600 shrink-0 mt-2" />
                </button>
              );
            })}
          </div>

          <p className="mt-6 text-xs text-gray-400 text-center">
            Each option downloads its own template. You can run them separately anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
